import { BrutalButton } from "./BrutalUI";
import Avatar from "./Avatar";
import type { AvatarBitmap } from "../avatars";

interface HeaderMinimalProps {
  title?: string;
}

export function HeaderMinimal({ title = "Meetday" }: HeaderMinimalProps) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b-4 border-black dark:border-gray-100 flex-shrink-0 bg-white dark:bg-gray-950">
      <h1 className="m-0 text-3xl sm:text-4xl font-logo font-bold text-gray-900 dark:text-white tracking-tight uppercase">
        {title}
      </h1>
      <BrutalButton
        type="button"
        variant="primary"
        className="min-h-[2.5rem] px-5 py-2.5"
        onClick={() => window.meetdayToggleTheme?.()}
        aria-label="Toggle light/dark theme"
      >
        Theme
      </BrutalButton>
    </header>
  );
}

const USER_ICON_SIZE = 48;

interface HeaderCalendarProps {
  slug: string;
  /** Optional human-readable calendar name derived from events. */
  calendarName?: string | null;
  /** Current user's face avatar; when absent, header shows default grid-of-dots face. */
  avatar?: AvatarBitmap | null;
  username: string;
  shareURL: string;
  /** Opens the rename calendar dialog. */
  onEditCalendarName?: () => void;
  /** Opens the settings dialog. */
  onOpenSettings: () => void;
}

export function HeaderCalendar({
  slug,
  calendarName,
  avatar,
  username,
  shareURL,
  onEditCalendarName,
  onOpenSettings,
}: HeaderCalendarProps) {
  const displayName = (calendarName && calendarName.trim()) || slug;

  const handleCopyShare = () => {
    if (!navigator.clipboard || !shareURL) return;
    navigator.clipboard.writeText(shareURL).then(() => {
      const el = document.querySelector(".share-link[data-copy-url]");
      if (el) {
        const orig = el.textContent;
        el.textContent = "Copied!";
        el.classList.add("share-link-copied");
        setTimeout(() => {
          el.textContent = orig ?? "";
          el.classList.remove("share-link-copied");
        }, 1500);
      }
    });
  };

  return (
    <header className="flex items-center justify-between gap-3 px-6 py-4 border-b-4 border-black dark:border-gray-100 flex-shrink-0 bg-white dark:bg-gray-950">
      <div className="flex-shrink-0">
        <a
          href="/"
          className="no-underline text-inherit hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brutal-orange focus:ring-offset-2"
          aria-label="Meetday home"
        >
          <h1 className="m-0 text-xl sm:text-2xl font-logo font-bold text-gray-900 dark:text-white tracking-tight uppercase whitespace-nowrap">
            Meetday
          </h1>
        </a>
      </div>
      <div className="flex-1 flex justify-center min-w-0 px-2">
        {onEditCalendarName ? (
          <button
            type="button"
            onClick={onEditCalendarName}
            title={displayName}
            className="min-h-[2.5rem] px-4 py-2 text-sm font-semibold rounded-none border-2 border-black dark:border-gray-100 bg-brutal-purple text-white hover:bg-brutal-purple-hover truncate max-w-full"
            aria-label={`Calendar: ${displayName}. Click to rename.`}
          >
            {displayName}
          </button>
        ) : (
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-full" title={displayName}>
            {displayName}
          </span>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 flex-shrink-0 w-24">
        <BrutalButton
          type="button"
          variant="secondary"
          className="share-link min-h-[2.5rem] px-4 py-2 text-sm font-semibold"
          data-copy-url={shareURL}
          title={shareURL}
          onClick={handleCopyShare}
          aria-label={`Copy share link ${shareURL}`}
        >
          Share
        </BrutalButton>
        <button
          type="button"
          onClick={onOpenSettings}
          className="flex items-center justify-center rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 p-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brutal-orange min-w-[2.75rem] min-h-[2.75rem]"
          title="Settings"
          aria-label="Open settings"
        >
          <Avatar username={username} avatar={avatar} size={USER_ICON_SIZE} />
        </button>
      </div>
    </header>
  );
}
